import {
  ChangeDetectionStrategy,
  Component,
  OnDestroy,
  OnInit,
  inject,
  signal,
} from '@angular/core';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { form, FormField, minLength, required } from '@angular/forms/signals';
import { Subscription } from 'rxjs';
import { FirebaseService } from '../../common/firebase/firebase.service';

@Component({
  selector: 'app-auth-action',
  imports: [FormField, RouterLink],
  templateUrl: './auth-action.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AuthActionComponent implements OnInit, OnDestroy {
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly firebaseService = inject(FirebaseService);
  private readonly subscriptionList = new Subscription();

  readonly mode = signal<string | null>(null);
  readonly oobCode = signal<string | null>(null);
  readonly isSubmitting = signal(false);
  readonly error = signal<string | null>(null);
  readonly success = signal(false);
  readonly passwordVisible = signal(false);

  readonly passwordModel = signal({
    password: '',
    confirmPassword: '',
  });

  readonly passwordForm = form(this.passwordModel, (schemaPath) => {
    required(schemaPath.password, { message: 'Password is required' });
    minLength(schemaPath.password, 6, { message: 'Password must be at least 6 characters' });
    required(schemaPath.confirmPassword, { message: 'Confirm your password' });
  });

  ngOnInit(): void {
    this.subscriptionList.add(
      this.route.queryParamMap.subscribe((params) => {
        this.mode.set(params.get('mode'));
        this.oobCode.set(params.get('oobCode'));
        this.error.set(null);
        this.success.set(false);

        if (params.get('mode') !== 'resetPassword' || !params.get('oobCode')) {
          this.error.set('This password reset link is invalid or incomplete.');
        }
      }),
    );
  }

  ngOnDestroy(): void {
    this.subscriptionList.unsubscribe();
  }

  submitReset(): void {
    const code = this.oobCode();
    const password = this.passwordModel().password;
    const confirm = this.passwordModel().confirmPassword;

    this.error.set(null);

    if (!code || this.mode() !== 'resetPassword') {
      this.error.set('This password reset link is invalid or incomplete.');
      return;
    }

    if (this.passwordForm().invalid()) {
      this.error.set('Enter a valid password (at least 6 characters).');
      return;
    }

    if (password !== confirm) {
      this.error.set('Passwords do not match.');
      return;
    }

    if (this.isSubmitting()) {
      return;
    }

    this.isSubmitting.set(true);
    this.subscriptionList.add(
      this.firebaseService.confirmPasswordReset(code, password).subscribe({
        next: () => {
          this.isSubmitting.set(false);
          this.success.set(true);
        },
        error: (err: { code?: string }) => {
          this.isSubmitting.set(false);
          this.error.set(this.resetErrorMessage(err?.code));
        },
      }),
    );
  }

  goToLogin(): void {
    void this.router.navigate(['/login']);
  }

  private resetErrorMessage(code: string | undefined): string {
    switch (code) {
      case 'auth/expired-action-code':
        return 'This reset link has expired. Please request a new one from the login page.';
      case 'auth/invalid-action-code':
        return 'This reset link is invalid or has already been used. Please request a new one.';
      case 'auth/weak-password':
        return 'Please choose a stronger password.';
      case 'auth/network-request-failed':
        return 'Network error. Please check your connection and try again.';
      default:
        return 'Could not reset your password. Please try again or request a new link.';
    }
  }
}
